// GlobalToolbar.jsx — Always-available chrome above main content

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useBase } from 'context/BaseContext';
import { useDatabase } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useShellChrome } from 'context/ShellChromeContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

import useBaseAccess from 'hooks/useBaseAccess';
import useBaseAccessLock from 'hooks/useBaseAccessLock';
import { useIsMobile } from 'hooks/useDeviceDetect';
import useSidebarSelection from 'main/sidebar/hooks/useSidebarSelection';

import { SectionType } from 'utilities/Enums';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';
import { openBaseInvite } from 'utilities/openBaseInvite';
import { openBaseSettings } from 'utilities/openBaseSettings';
import { buildBaseMemberProfilePath, getSectionPath } from 'utilities/Routing';
import { normalizeSearchQuery } from 'services/search/BaseNodeSearchService';
import { buildAiFeedChannelKey } from 'spaces/utils/aiChannelKeys';
import {
  buildAiHomePath,
  getAiSessionKeyFromLocation,
} from 'spaces/utils/buildAiHomePath';
import { AI_ICON_KEY, AI_ICON_PATH } from 'svg/customIconPaths';

import ThemeToggle from 'components/themetoggle/ThemeToggle';
import HelpPanel from 'help/panel/HelpPanel';
import useHelpContext from 'help/hooks/useHelpContext';
import { HELP_ICON_PATH } from 'utilities/HelpArticles';
import Icon from 'svg/Icon';

import './GlobalToolbar.css';

/**
 * GlobalToolbar
 *
 * Desktop chrome above the main content column. Find field aligned left;
 * global destination / utility icon buttons aligned right (same destinations
 * as the mobile sidebar primary nav, plus theme toggle).
 *
 * @returns {ReactNode}
 */
const GlobalToolbar = React.memo(() => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const {
    user,
    userSettings,
    userUnreadNotifications,
  } = useUser();
  const { activeBase } = useBase();
  const { setCurrentNode, setCurrentSection, currentSection } = useNavigation();
  const { setSelectedLocation } = useUI();
  const { setActiveDatabase } = useDatabase();
  const { setGlobalLeadingSlotEl } = useShellChrome();

  // ----- DEVICE / ROUTING ----- //

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ----- HOOKS ----- //

  const { accessLocked: baseNavigationLocked } = useBaseAccessLock();
  const { baseTier, can } = useBaseAccess(activeBase);
  const canSeeBaseSettings = baseTier === 'owner' || baseTier === 'admin';
  const canSeeSources = can('sources.manage') || can('sources.read');
  const canOpenSettings = canSeeBaseSettings || canSeeSources;
  const canInviteMembers = can('invite.create') && !baseNavigationLocked;

  // ----- STATE ----- //

  const [searchFocused, setSearchFocused] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [hoveredKey, setHoveredKey] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const { typeKey: helpTypeKey, typeLabel: helpTypeLabel } = useHelpContext();

  // ----- REFS ----- //

  const searchInputRef = useRef(null);

  // ----- MEMOS ----- //

  const preferredBaseKeyFromSettings = getActiveBaseKeyFromSettings(userSettings);
  const activeBaseKey = useMemo(() => {
    const key = activeBase?.key || activeBase?.id || preferredBaseKeyFromSettings || '';
    return String(key).trim();
  }, [activeBase?.id, activeBase?.key, preferredBaseKeyFromSettings]);

  const unreadNotificationsCount = useMemo(() => {
    if (!activeBaseKey || !Array.isArray(userUnreadNotifications)) return 0;
    return userUnreadNotifications.filter((notification) => {
      if (!notification || notification.isRead === true) return false;
      return String(notification?.baseKey || '').trim() === activeBaseKey;
    }).length;
  }, [activeBaseKey, userUnreadNotifications]);

  const isSearchRoute = currentSection === SectionType.SEARCH
    || String(location.pathname || '').includes('/search');

  const {
    isSectionSelected,
    isOwnProfileSelected,
  } = useSidebarSelection({
    user,
    activeBaseKey,
  });

  // ----- EFFECTS ----- //

  // Keep draft in sync when already on Search with a URL query.
  useEffect(() => {
    if (!isSearchRoute) return;
    setSearchDraft(normalizeSearchQuery(searchParams.get('q') || ''));
  }, [isSearchRoute, searchParams]);

  // ⌘K / Ctrl+K focuses the find field.
  useEffect(() => {
    if (isMobile) return undefined;

    /**
     * Focuses the global find field on ⌘K / Ctrl+K.
     *
     * @param {KeyboardEvent} event
     */
    const handleKeyDown = (event) => {
      const key = String(event.key || '').toLowerCase();
      if (key !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey) return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable) return;
      event.preventDefault();
      searchInputRef.current?.focus?.();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  // ----- HANDLERS ----- //

  /**
   * Clears node selection and navigates to a section when unlocked.
   *
   * @param {string} section
   * @param {string} path
   */
  const navigateSection = useCallback((section, path) => {
    if (baseNavigationLocked) {
      setActiveDatabase(null);
      setCurrentNode(null);
      setSelectedLocation(null);
      setCurrentSection(SectionType.HOME);
      if (location.pathname !== '/') navigate('/', { replace: true });
      return;
    }
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(section);
    navigate(path);
  }, [
    baseNavigationLocked,
    location.pathname,
    navigate,
    setActiveDatabase,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
  ]);

  /**
   * Opens Home.
   */
  const handleHomeClick = useCallback(() => {
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.HOME);
    navigate(getSectionPath(SectionType.HOME));
  }, [navigate, setCurrentNode, setCurrentSection, setSelectedLocation]);

  /**
   * Opens the current user's profile in this base.
   */
  const handleProfileClick = useCallback(() => {
    if (baseNavigationLocked) {
      navigateSection(SectionType.HOME, '/');
      return;
    }
    const currentUserKey = String(user?.key || user?.userKey || '').trim();
    if (!activeBaseKey || !currentUserKey) return;

    setActiveDatabase(null);
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.USER);
    navigate(buildBaseMemberProfilePath(activeBaseKey, currentUserKey));
  }, [
    activeBaseKey,
    baseNavigationLocked,
    navigate,
    navigateSection,
    setActiveDatabase,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    user?.key,
    user?.userKey,
  ]);

  /**
   * Opens the Appdeck AI conversation for the current user.
   */
  const handleAiClick = useCallback(() => {
    if (baseNavigationLocked) {
      navigateSection(SectionType.HOME, '/');
      return;
    }
    const currentUserKey = String(user?.key || user?.userKey || '').trim();
    const aiChannelKey = buildAiFeedChannelKey(activeBaseKey, currentUserKey);
    if (!aiChannelKey) return;

    setActiveDatabase(null);
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.HOME);
    navigate(buildAiHomePath({ channelKey: aiChannelKey, baseKey: activeBaseKey }));
  }, [
    activeBaseKey,
    baseNavigationLocked,
    navigate,
    navigateSection,
    setActiveDatabase,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    user?.key,
    user?.userKey,
  ]);

  /**
   * Opens Notifications.
   */
  const handleNotificationsClick = useCallback(() => {
    navigateSection(SectionType.NOTIFICATIONS, '/notifications');
  }, [navigateSection]);

  /**
   * Opens Schedule.
   */
  const handleScheduleClick = useCallback(() => {
    navigateSection(SectionType.SCHEDULE, '/schedule');
  }, [navigateSection]);

  /**
   * Opens the invite flow.
   */
  const handleInviteClick = useCallback(() => {
    if (!canInviteMembers) return;
    openBaseInvite();
  }, [canInviteMembers]);

  /**
   * Opens Customize (Studio).
   */
  const handleCustomizeClick = useCallback(() => {
    navigateSection(SectionType.STUDIO, getSectionPath(SectionType.STUDIO));
  }, [navigateSection]);

  /**
   * Opens Trash.
   */
  const handleTrashClick = useCallback(() => {
    navigateSection(SectionType.TRASH, '/trash');
  }, [navigateSection]);

  /**
   * Opens the Search section (icon-only fallback).
   */
  const handleSearchIconClick = useCallback(() => {
    navigateSection(SectionType.SEARCH, getSectionPath(SectionType.SEARCH));
  }, [navigateSection]);

  /**
   * Submits the find field into the Search page.
   */
  const submitSearch = useCallback(() => {
    if (baseNavigationLocked) {
      navigateSection(SectionType.HOME, '/');
      return;
    }
    const q = normalizeSearchQuery(searchDraft);
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.SEARCH);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const query = params.toString();
    navigate(query ? `/search?${query}` : '/search');
  }, [
    baseNavigationLocked,
    navigate,
    navigateSection,
    searchDraft,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
  ]);

  /**
   * Opens base settings when the user can manage the base or sources.
   */
  const handleSettingsClick = useCallback(() => {
    if (!canOpenSettings || baseNavigationLocked) return;
    openBaseSettings();
  }, [baseNavigationLocked, canOpenSettings]);

  /**
   * Builds icon-button styles matching MenuButton / node-workspace-header.
   *
   * @param {string} key
   * @param {boolean} selected
   * @returns {object}
   */
  const chromeButtonStyle = useCallback((key, selected) => {
    const hovered = hoveredKey === key;
    return {
      backgroundColor: hovered ? theme.buttonHoverBackgroundColor : 'transparent',
      color: hovered
        ? theme.buttonHoverLabelColor
        : (selected ? theme.foreground : theme.foregroundMuted),
    };
  }, [hoveredKey, theme]);

  /**
   * Icon color for a toolbar button.
   *
   * @param {string} key
   * @param {boolean} selected
   * @returns {string}
   */
  const chromeIconColor = useCallback((key, selected) => {
    if (hoveredKey === key) return theme.buttonHoverLabelColor;
    if (selected) return theme.foreground;
    return theme.foregroundMuted;
  }, [hoveredKey, theme]);

  /**
   * Publishes the leading slot (left of Find) into ShellChromeContext.
   *
   * @param {HTMLElement|null} node
   */
  const handleLeadingSlotRef = useCallback((node) => {
    setGlobalLeadingSlotEl(node);
  }, [setGlobalLeadingSlotEl]);

  // ----- RENDER ----- //

  if (isMobile || !activeBaseKey) return null;

  const currentUserKey = String(user?.key || user?.userKey || '').trim();
  const aiSelected = Boolean(
    getAiSessionKeyFromLocation(location, activeBaseKey, currentUserKey)
  );
  const canOpenAi = Boolean(activeBaseKey && currentUserKey);

  const homeSelected = isSectionSelected(SectionType.HOME) && !aiSelected;
  const profileSelected = isOwnProfileSelected;
  const notificationsSelected = isSectionSelected(SectionType.NOTIFICATIONS);
  const scheduleSelected = isSectionSelected(SectionType.SCHEDULE);
  const customizeSelected = isSectionSelected(SectionType.STUDIO);
  const trashSelected = isSectionSelected(SectionType.TRASH);
  const canOpenProfile = Boolean(activeBaseKey && (user?.key || user?.userKey));

  return (
    <div
      className="global-toolbar-container"
      role="toolbar"
      aria-label="Global"
    >

      {/* LEADING — optional Back etc. via ShellGlobalLeading */}
      <div
        ref={handleLeadingSlotRef}
        className="global-toolbar-leading"
      />

      {/* FIND */}
      <div className="global-toolbar-find">

        {/* SEARCH FIELD */}
        <div
          className={[
            'global-toolbar-search',
            searchFocused ? 'is-focused' : '',
          ].filter(Boolean).join(' ')}
          style={{
            borderColor: searchFocused ? theme.foregroundFaded : theme.outlineMuted,
          }}
        >
          <Icon
            iconKey="search"
            color={theme.foregroundMuted}
            width={18}
            height={18}
          />
          <input
            ref={searchInputRef}
            className="global-toolbar-search-input"
            style={{ color: theme.foreground }}
            type="search"
            value={searchDraft}
            placeholder="Find…"
            aria-label="Find in this knowledge base"
            enterKeyHint="search"
            onBlur={() => setSearchFocused(false)}
            onChange={(event) => setSearchDraft(event.target.value)}
            onFocus={() => setSearchFocused(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitSearch();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                setSearchDraft('');
                searchInputRef.current?.blur?.();
              }
            }}
          />
          <kbd
            className="global-toolbar-search-hint"
            style={{ color: theme.foregroundFaded }}
          >
            ⌘K
          </kbd>
        </div>

        {/* SEARCH ICON FALLBACK */}
        <button
          className="global-toolbar-button global-toolbar-search-fallback"
          style={chromeButtonStyle('search', isSearchRoute)}
          type="button"
          aria-label="Search"
          title="Search"
          onClick={handleSearchIconClick}
          onMouseEnter={() => setHoveredKey('search')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="search"
            color={chromeIconColor('search', isSearchRoute)}
            width={20}
            height={20}
          />
        </button>

        {/* HELP */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('help', helpOpen)}
          type="button"
          aria-label="Help"
          title="Help"
          onClick={() => setHelpOpen(true)}
          onMouseEnter={() => setHoveredKey('help')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="help"
            path={HELP_ICON_PATH}
            color={chromeIconColor('help', helpOpen)}
            width={20}
            height={20}
          />
        </button>

        {/* THEME */}
        <ThemeToggle />
      </div>

      {/* ACTIONS */}
      <div className="global-toolbar-actions">

        {/* HOME */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('home', homeSelected)}
          type="button"
          aria-label="Home"
          title="Home"
          onClick={handleHomeClick}
          onMouseEnter={() => setHoveredKey('home')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="home"
            color={chromeIconColor('home', homeSelected)}
            width={20}
            height={20}
          />
        </button>

        {/* AI */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('ai', aiSelected)}
          type="button"
          aria-label="AI"
          title="AI"
          disabled={baseNavigationLocked || !canOpenAi}
          onClick={handleAiClick}
          onMouseEnter={() => setHoveredKey('ai')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey={AI_ICON_KEY}
            path={AI_ICON_PATH}
            color={chromeIconColor('ai', aiSelected)}
            width={20}
            height={20}
          />
        </button>

        {/* PROFILE */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('profile', profileSelected)}
          type="button"
          aria-label="Profile"
          title="Profile"
          disabled={baseNavigationLocked || !canOpenProfile}
          onClick={handleProfileClick}
          onMouseEnter={() => setHoveredKey('profile')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="person"
            color={chromeIconColor('profile', profileSelected)}
            width={20}
            height={20}
          />
        </button>

        {/* NOTIFICATIONS */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('notifications', notificationsSelected)}
          type="button"
          aria-label="Notifications"
          title="Notifications"
          disabled={baseNavigationLocked}
          onClick={handleNotificationsClick}
          onMouseEnter={() => setHoveredKey('notifications')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="notifications"
            color={chromeIconColor('notifications', notificationsSelected)}
            width={20}
            height={20}
          />
          {unreadNotificationsCount > 0 ? (
            <span
              className="global-toolbar-badge"
              style={{ color: theme.highlightForeground || theme.foreground }}
            >
              {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
            </span>
          ) : null}
        </button>

        {/* SCHEDULE */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('schedule', scheduleSelected)}
          type="button"
          aria-label="Schedule"
          title="Schedule"
          disabled={baseNavigationLocked}
          onClick={handleScheduleClick}
          onMouseEnter={() => setHoveredKey('schedule')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="event"
            color={chromeIconColor('schedule', scheduleSelected)}
            width={20}
            height={20}
          />
        </button>

        {/* INVITE */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('invite', false)}
          type="button"
          aria-label="Invite"
          title="Invite"
          disabled={!canInviteMembers}
          onClick={handleInviteClick}
          onMouseEnter={() => setHoveredKey('invite')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="person_add"
            color={chromeIconColor('invite', false)}
            width={20}
            height={20}
          />
        </button>

        {/* SETTINGS */}
        {canOpenSettings ? (
          <button
            className="global-toolbar-button"
            style={chromeButtonStyle('settings', false)}
            type="button"
            aria-label="Settings"
            title="Settings"
            disabled={baseNavigationLocked}
            onClick={handleSettingsClick}
            onMouseEnter={() => setHoveredKey('settings')}
            onMouseLeave={() => setHoveredKey(null)}
          >
            <Icon
              iconKey="settings"
              color={chromeIconColor('settings', false)}
              width={20}
              height={20}
            />
          </button>
        ) : null}

        {/* CUSTOMIZE
        {canSeeBaseSettings ? (
          <button
            className="global-toolbar-button"
            style={chromeButtonStyle('customize', customizeSelected)}
            type="button"
            aria-label="Customize"
            title="Customize"
            disabled={baseNavigationLocked}
            onClick={handleCustomizeClick}
            onMouseEnter={() => setHoveredKey('customize')}
            onMouseLeave={() => setHoveredKey(null)}
          >
            <Icon
              iconKey="instant_mix"
              color={chromeIconColor('customize', customizeSelected)}
              width={20}
              height={20}
            />
          </button>
        ) : null}
          */}

        {/* RECYCLE BIN */}
        <button
          className="global-toolbar-button"
          style={chromeButtonStyle('trash', trashSelected)}
          type="button"
          aria-label="Recycle Bin"
          title="Recycle Bin"
          disabled={baseNavigationLocked}
          onClick={handleTrashClick}
          onMouseEnter={() => setHoveredKey('trash')}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <Icon
            iconKey="recycling"
            color={chromeIconColor('trash', trashSelected)}
            width={20}
            height={20}
          />
        </button>
        
      </div>

      <HelpPanel
        isOpen={helpOpen}
        typeKey={helpTypeKey}
        typeLabel={helpTypeLabel}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
});

GlobalToolbar.displayName = 'GlobalToolbar';

export default GlobalToolbar;
